import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ImageDropzone, InputField, LoaderButton } from 'components/molecules'
import { Form, Formik } from 'formik'
import { SELLER_IDENTITY_TYPES } from 'domains/seller/constants'
import { Button, Label } from 'reactstrap'
import FileResizer from 'react-image-file-resizer'
import sellerIdentityProofFormSchema from './seller-identity-proof-form-schema'


const propTypes = {
  sellerIdentityProof : PropTypes.object,
  onClickSubmit       : PropTypes.func,
  disabled            : PropTypes.bool
}

const defaultProps = {
  sellerIdentityProof : {},
  onClickSubmit       : () => { },
  disabled            : false
}

/**
 *
 * @param {object} sellerIdentityProof - object that contains data of ::Seller.identity_proof
 * @param {function} onClickSubmit  - function to handle submission of this form
 * @param {boolean} disabled  - boolean to disable update button
 * @returns
 */
const SellerIdentityProofForm = ({
  sellerIdentityProof,
  onClickSubmit,
  disabled
}) => {

  const [ identityProofImage, setIdentityProofImage ] = useState()

  // Set Default Identity and Image
  useEffect(() => {
    if (sellerIdentityProof) {
      setIdentityProofImage(sellerIdentityProof.s3_url)
    }
  }, [ sellerIdentityProof ])

  /**
   * Resizes images to max width or height of 1280 pixels
   *
   * @param {Array<File>} files - dropzone returns array of files even if isMulti=false
   * @param {object} arrayHelpers - Formik's FieldArray helpers
   */
  const handleDropImage = async (files, formik) => {
    const resizedFilePromises = files.map((file) => resizeFile(file))
    const resizedFiles        = await Promise.all(resizedFilePromises)
      .then((resolvedPromise) => resolvedPromise)
    setIdentityProofImage(URL.createObjectURL(resizedFiles[0]))
    formik.setFieldValue('identity_proof_image', resizedFiles)
  }

  const handleClickRemoveImage = (formik) => {
    setIdentityProofImage(undefined)
    formik.setFieldValue('identity_proof_image','')
  }

  /**
   * Resize the file to max 1280px on the width or height maintaining aspect ratio
   * @param {file} file - selected file
   * @returns {Promise}
   */
  const resizeFile = (file) => (
    new Promise((resolve) => {
      FileResizer.imageFileResizer(
        file,
        1280,
        1280,
        'JPEG',
        100,
        0,
        (uri) => {
          resolve(uri)
        },
        'file',
        1280
      )
    })
  )

  const handleClickRemove = (formik, fieldName) => {
    formik.setFieldValue(fieldName, '')
  }

  return (
    <Formik
      initialValues={{
        identity_type        : sellerIdentityProof?.identity_type || 'national_identity',
        identity_proof_image : []
      }}
      enableReinitialize={ true }
      validationSchema={ sellerIdentityProofFormSchema }
      onSubmit={ onClickSubmit }
    >
      {
        (formik) => (
          <Form>
            <InputField
              type='select'
              name='identity_type'
              label='Jenis Identitas'
            >
              <option value={ '' } disabled>
                { 'Pilih jenis identitas' }
              </option>
              { SELLER_IDENTITY_TYPES.map((identityType) => (
                <option
                  key={ `identity-type-${identityType.value}` }
                  value={ identityType.value }
                >
                  { identityType.label }
                </option>
              )) }
            </InputField>

            <Label
              for={ 'identity_proof_image' }
              className='text-capitalize form-control-label font-weight-bold'
            >
              { 'Foto Identitas' }
            </Label>
            {
              identityProofImage
                ? (
                  <div className='mb-3 align-items-start'>
                    <img
                      src={ identityProofImage }
                      alt="Identity Proof Image"
                      width={ '50%' }
                      style={{
                        pointerEvents: 'none'
                      }}
                    />
                    <Button
                      color={ 'danger' }
                      className='rounded ml-3'
                      onClick={ () => handleClickRemoveImage(formik) }
                    >
                      <i className='fas fa-trash'/>
                    </Button>
                  </div>
                )
                : (
                  <div
                    className='mb-3'
                    style={{
                      width         : '20rem',
                      border        : '1px dashed grey',
                      borderRadius  : '10px',
                      boxSizing     : 'border-box',
                      display       : 'flex',
                      flexDirection : 'column',
                      textAlign     : 'center',
                      paddingTop    : '15px'
                    }}
                  >
                    <ImageDropzone
                      name={ 'identity_proof_image' }
                      placeholderImg={ '/img/image-dropzone-placeholder.svg' }
                      onDrop={ (files) => handleDropImage(files, formik) }
                      onClickRemove={ () => handleClickRemove(formik, 'identity_proof_image') }
                    />
                    <small
                      style={{ fontSize: '0.67rem' }}
                      className='d-block text-center text-capitalize font-weight-light text-gray'
                    >
                      { 'Foto Identitas' }
                    </small>
                  </div>
                )
            }
            <LoaderButton
              className={ 'rounded float-right' }
              type={ 'submit' }
              color={ 'primary' }
              disabled={ disabled }
              text={ 'Update Identity Proof' }
            />
          </Form>
        )
      }
    </Formik>
  )
}

SellerIdentityProofForm.propTypes    = propTypes
SellerIdentityProofForm.defaultProps = defaultProps

export default SellerIdentityProofForm
